/**
 * Transaction process graph for product offers:
 *   - product-offer
 *
 * This process allows customers to make price offers on product listings.
 * The provider (seller) creates the listing, and the customer (buyer) initiates
 * by making an offer. Both parties can counter-offer back and forth until
 * they agree on a price, at which point the customer pays and the standard
 * product delivery flow begins.
 *
 * Flow:
 *   Customer makes offer → Provider accepts/rejects/counters →
 *   (negotiation loop) → Payment → Delivery → Received → Complete → Review
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // Customer initiates by making an offer on a product listing.
  CUSTOMER_MAKE_OFFER: 'transition/customer-make-offer',

  // Provider can accept the customer's offer directly.
  PROVIDER_ACCEPT_OFFER: 'transition/provider-accept-offer',

  // Provider can reject the customer's offer.
  PROVIDER_REJECT_OFFER: 'transition/provider-reject-offer',

  // Provider can make a counter-offer with a different price.
  PROVIDER_COUNTER_OFFER: 'transition/provider-counter-offer',

  // Customer can accept the provider's counter-offer.
  CUSTOMER_ACCEPT_COUNTER_OFFER: 'transition/customer-accept-counter-offer',

  // Customer can reject the provider's counter-offer.
  CUSTOMER_REJECT_COUNTER_OFFER: 'transition/customer-reject-counter-offer',

  // Customer can make a new counter-offer in response to provider's counter.
  CUSTOMER_COUNTER_OFFER: 'transition/customer-counter-offer',

  // Customer can withdraw their offer.
  CUSTOMER_WITHDRAW_OFFER: 'transition/customer-withdraw-offer',

  // Customer can withdraw from counter-offer negotiation.
  CUSTOMER_WITHDRAW_FROM_COUNTER_OFFER: 'transition/customer-withdraw-offer-from-counter',

  // Operator can reject offers at various stages.
  OPERATOR_REJECT_OFFER: 'transition/operator-reject-offer',
  OPERATOR_REJECT_COUNTER_OFFER: 'transition/operator-reject-counter-offer',

  // Once offer is accepted, customer initiates payment.
  REQUEST_PAYMENT: 'transition/request-payment',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in time (default 15min), the transaction expires.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // Provider or operator can mark the product shipped/delivered.
  MARK_DELIVERED: 'transition/mark-delivered',
  OPERATOR_MARK_DELIVERED: 'transition/operator-mark-delivered',

  // Customer can mark the product received (e.g. picked up from provider).
  MARK_RECEIVED_FROM_PURCHASED: 'transition/mark-received-from-purchased',

  // Automatic cancellation if no one marks delivery.
  AUTO_CANCEL: 'transition/auto-cancel',

  // Operator can cancel the purchase before delivery.
  CANCEL: 'transition/cancel',

  // Customer marks the product received after delivery.
  MARK_RECEIVED: 'transition/mark-received',

  // Auto mark received if customer doesn't do it manually.
  AUTO_MARK_RECEIVED: 'transition/auto-mark-received',

  // Customer or operator can dispute after delivery.
  DISPUTE: 'transition/dispute',
  OPERATOR_DISPUTE: 'transition/operator-dispute',

  // Disputed transaction auto-cancels if nothing is done.
  AUTO_CANCEL_FROM_DISPUTED: 'transition/auto-cancel-from-disputed',

  // Operator can cancel or resolve disputed transaction.
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',

  // Operator can mark disputed transaction as received.
  MARK_RECEIVED_FROM_DISPUTED: 'transition/mark-received-from-disputed',

  // System auto-completes after received.
  AUTO_COMPLETE: 'transition/auto-complete',

  // Reviews
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  OFFER_PENDING: 'offer-pending',
  COUNTER_OFFER_PENDING: 'counter-offer-pending',
  OFFER_ACCEPTED: 'offer-accepted',
  OFFER_REJECTED: 'offer-rejected',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PURCHASED: 'purchased',
  DELIVERED: 'delivered',
  RECEIVED: 'received',
  DISPUTED: 'disputed',
  CANCELED: 'canceled',
  COMPLETED: 'completed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
  REVIEWED: 'reviewed',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'product-offer/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.CUSTOMER_MAKE_OFFER]: states.OFFER_PENDING,
      },
    },

    // Customer has made an offer, waiting for provider response
    [states.OFFER_PENDING]: {
      on: {
        [transitions.PROVIDER_ACCEPT_OFFER]: states.OFFER_ACCEPTED,
        [transitions.PROVIDER_REJECT_OFFER]: states.OFFER_REJECTED,
        [transitions.PROVIDER_COUNTER_OFFER]: states.COUNTER_OFFER_PENDING,
        [transitions.CUSTOMER_WITHDRAW_OFFER]: states.OFFER_REJECTED,
        [transitions.OPERATOR_REJECT_OFFER]: states.OFFER_REJECTED,
      },
    },

    // Provider has made a counter-offer, waiting for customer response
    [states.COUNTER_OFFER_PENDING]: {
      on: {
        [transitions.CUSTOMER_ACCEPT_COUNTER_OFFER]: states.OFFER_ACCEPTED,
        [transitions.CUSTOMER_REJECT_COUNTER_OFFER]: states.OFFER_PENDING,
        [transitions.CUSTOMER_COUNTER_OFFER]: states.OFFER_PENDING,
        [transitions.CUSTOMER_WITHDRAW_FROM_COUNTER_OFFER]: states.OFFER_REJECTED,
        [transitions.OPERATOR_REJECT_COUNTER_OFFER]: states.OFFER_REJECTED,
      },
    },

    // Both parties have agreed on a price, customer can now pay
    [states.OFFER_ACCEPTED]: {
      on: {
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PURCHASED,
      },
    },

    [states.PAYMENT_EXPIRED]: { type: 'final' },

    [states.PURCHASED]: {
      on: {
        [transitions.MARK_DELIVERED]: states.DELIVERED,
        [transitions.OPERATOR_MARK_DELIVERED]: states.DELIVERED,
        [transitions.MARK_RECEIVED_FROM_PURCHASED]: states.RECEIVED,
        [transitions.AUTO_CANCEL]: states.CANCELED,
        [transitions.CANCEL]: states.CANCELED,
      },
    },

    [states.CANCELED]: { type: 'final' },

    [states.DELIVERED]: {
      on: {
        [transitions.MARK_RECEIVED]: states.RECEIVED,
        [transitions.AUTO_MARK_RECEIVED]: states.RECEIVED,
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.AUTO_CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.MARK_RECEIVED_FROM_DISPUTED]: states.RECEIVED,
      },
    },

    [states.RECEIVED]: {
      on: {
        [transitions.AUTO_COMPLETE]: states.COMPLETED,
      },
    },

    [states.COMPLETED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
    [states.OFFER_REJECTED]: { type: 'final' },
  },
};

// Transitions where an initial offer is made
const makeOfferTransitions = [
  'transition/make-offer',
  'transition/make-offer-after-inquiry',
  'transition/make-offer-from-request',
  'transition/customer-make-offer',
  'transition/make-offer-from-product-offer',
];

// Transitions where a counter-offer is made
const counterOfferTransitions = [
  'transition/customer-make-counter-offer',
  'transition/provider-make-counter-offer',
  'transition/provider-counter-offer',
  'transition/customer-counter-offer',
];

// Transitions where a counter-offer is revoked/rejected (reverting to previous offer)
const revokeCounterOfferTransitions = [
  'transition/customer-withdraw-counter-offer',
  'transition/provider-reject-counter-offer',
  'transition/customer-reject-counter-offer',
  'transition/customer-withdraw-offer',
  'transition/customer-withdraw-offer-from-counter',
];

// All transitions that affect pricing in the negotiation loop
const offerTransitions = [
  ...makeOfferTransitions,
  ...counterOfferTransitions,
  ...revokeCounterOfferTransitions,
];

const filterOfferTransitions = (transitions, offerTransitions) => {
  return transitions.filter(t => offerTransitions.includes(t.transition));
};

/**
 * @typedef {Object} NegotiationOffer
 * @property {string} transition - The transition name that was triggered to make this offer
 * @property {string} by - The actor who made the offer ('provider' or 'customer')
 * @property {number} offerInSubunits - The offer amount in subunits (smallest currency unit)
 */

/**
 * @typedef {Object} TransitionRecord
 * @property {string} transition - The transition name
 * @property {string} by - The actor who made the transition ('provider' or 'customer')
 * @property {string} createdAt - ISO timestamp when the transition was created
 */

/**
 * @typedef {Object} TransitionWithOfferInSubunitsRecord
 * @property {string} transition - The transition name
 * @property {string} by - The actor who made the transition ('provider' or 'customer')
 * @property {string} createdAt - ISO timestamp when the transition was created
 * @property {number} [offerInSubunits] - The offer amount in subunits (smallest currency unit)
 */

/**
 * Checks if the offers array is valid against the (past) transitions array.
 *
 * @param {Array<TransitionRecord>} transitions
 * @param {Array<NegotiationOffer>} offers
 * @returns {boolean}
 */
export const isValidNegotiationOffersArray = (transitions, offers) => {
  const pickedTransitions = filterOfferTransitions(transitions, offerTransitions);
  const isOffersAnArray = !!offers && Array.isArray(offers);

  // First check if we have the same number of offers and transitions
  if (!isOffersAnArray || offers.length !== pickedTransitions.length) {
    return false;
  }

  // Then verify that each offer corresponds to the transition at the same index
  for (let i = 0; i < offers.length; i++) {
    const offer = offers[i];
    const transition = pickedTransitions[i];

    if (offer.transition !== transition.transition || offer.by !== transition.by) {
      return false;
    }
  }

  return true;
};

/**
 * Checks if the state is a negotiation state.
 * Negotiation states are states where the user can make an offer.
 *
 * @param {string} state
 * @returns {boolean}
 */
export const isNegotiationState = state => {
  if (state == null) {
    return false;
  }
  const unprefixedState = state.indexOf('/') === -1 ? state : state.split('/')[1];
  return [states.OFFER_PENDING, states.COUNTER_OFFER_PENDING].includes(unprefixedState);
};

/**
 * Returns a new array of transitions with the offerInSubunits property added
 * to the transitions that have a matching offer.
 *
 * @param {Array<TransitionRecord>} transitions
 * @param {Array<NegotiationOffer>} offers
 * @returns {Array<TransitionWithOfferInSubunitsRecord>}
 */
export const getTransitionsWithMatchingOffers = (transitions, offers) => {
  const isValidOffersArray = isValidNegotiationOffersArray(transitions, offers);
  if (isValidOffersArray) {
    const transitionsWithOffers = [];
    let offerIndex = 0;
    for (let i = 0; i < transitions.length; i++) {
      let transition = { ...transitions[i] };

      if (offerTransitions.includes(transition.transition)) {
        transition.offerInSubunits = offers[offerIndex]?.offerInSubunits;
        transitionsWithOffers.push(transition);
        offerIndex++;
      } else {
        transitionsWithOffers.push(transition);
      }
    }
    return transitionsWithOffers;
  }

  return transitions;
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
export const isRelevantPastTransition = transition => {
  return [
    transitions.CUSTOMER_MAKE_OFFER,
    transitions.PROVIDER_ACCEPT_OFFER,
    transitions.PROVIDER_REJECT_OFFER,
    transitions.PROVIDER_COUNTER_OFFER,
    transitions.CUSTOMER_ACCEPT_COUNTER_OFFER,
    transitions.CUSTOMER_REJECT_COUNTER_OFFER,
    transitions.CUSTOMER_COUNTER_OFFER,
    transitions.CUSTOMER_WITHDRAW_OFFER,
    transitions.CUSTOMER_WITHDRAW_FROM_COUNTER_OFFER,
    transitions.OPERATOR_REJECT_OFFER,
    transitions.OPERATOR_REJECT_COUNTER_OFFER,
    transitions.CONFIRM_PAYMENT,
    transitions.AUTO_CANCEL,
    transitions.CANCEL,
    transitions.MARK_DELIVERED,
    transitions.OPERATOR_MARK_DELIVERED,
    transitions.MARK_RECEIVED,
    transitions.MARK_RECEIVED_FROM_PURCHASED,
    transitions.DISPUTE,
    transitions.OPERATOR_DISPUTE,
    transitions.AUTO_COMPLETE,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.MARK_RECEIVED_FROM_DISPUTED,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is enough.
export const isPrivileged = transition => {
  return [
    transitions.CUSTOMER_MAKE_OFFER,
    transitions.PROVIDER_COUNTER_OFFER,
    transitions.CUSTOMER_COUNTER_OFFER,
    transitions.CUSTOMER_REJECT_COUNTER_OFFER,
    transitions.PROVIDER_ACCEPT_OFFER,
    transitions.CUSTOMER_ACCEPT_COUNTER_OFFER,
    transitions.REQUEST_PAYMENT,
  ].includes(transition);
};

// Check when transaction is completed (item is received and review notifications sent)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.AUTO_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (order did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.CANCEL,
    transitions.AUTO_CANCEL,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.OFFER_PENDING, states.PURCHASED];

export const statesNeedingCustomerAttention = [states.COUNTER_OFFER_PENDING, states.DELIVERED];
